import { Box, Divider, IconButton, Menu, styled, Tooltip, Typography } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import ContrastIcon from "@mui/icons-material/Contrast";
import TextDecreaseIcon from "@mui/icons-material/TextDecrease";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import { TextSize, ThemeMode, useTheme } from "@/app/contexts/theme-context";
import Grid from "@mui/material/Grid2";

interface A11yMenu {
    open: boolean;
    anchor: null | HTMLElement;
    onClose: () => void;
}

export default function A11yMenu({ open, anchor, onClose }: A11yMenu) {
    const { themeMode, setThemeMode, textSize, setTextSize } = useTheme();

    const handleThemeChange = (mode: ThemeMode) => {
        setThemeMode(mode);
    };

    const handleTextSizeChange = (size: TextSize) => {
        setTextSize(size);
    };

    const themeOptions = [
        { mode: 'dark', label: 'Dunkel', tooltip: 'Dunkles Design', Icon: DarkModeIcon },
        { mode: 'light', label: 'Hell', tooltip: 'Helles Design', Icon: LightModeIcon },
        { mode: 'high-contrast', label: 'Kontrast', tooltip: 'Hoher Kontrast', Icon: ContrastIcon },
    ] as const;

    return (
        <StyledMenu
            id="a11y-menu"
            anchorEl={anchor}
            open={open}
            onClose={onClose}
            style={{
                borderBottomLeftRadius: '50%'
            }}
            MenuListProps={{
                'aria-labelledby': 'a11y-button',
                role: 'menu',
            }}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            <Typography sx={{ px: 2, pb: 2 }} variant="h4" color="text.primary" role="presentation">
                Barrierefreiheit
            </Typography>

            {/* Theme Section */}
            <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary" role="presentation">
                    Erscheinungsbild
                </Typography>
                <Grid container spacing={1} columns={6} textAlign="center">
                    {themeOptions.map(({ mode, label, tooltip, Icon }) => (
                        <Grid key={mode} size={2}>
                            <Tooltip title={tooltip}>
                                <IconButton
                                    onClick={() => handleThemeChange(mode)}
                                    aria-label={tooltip}
                                    aria-pressed={themeMode === mode}
                                    color={themeMode === mode ? 'primary' : 'default'}
                                    size="large"
                                    sx={{
                                        width: '100%',
                                        border: theme => themeMode === mode ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                        borderRadius: '8px',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Icon />
                                    <Typography variant="body1" sx={{ mt: 0.5 }}>{label}</Typography>
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Text Size Section */}
            <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary" role="presentation">
                    Text Size
                </Typography>
                <Grid container spacing={1} columns={3} sx={{ mt: 0.5 }}>
                    <Grid size={1}>
                        <Tooltip title="Kleiner Text">
                            <IconButton
                                onClick={() => handleTextSizeChange('small')}
                                aria-label="Kleiner Text"
                                aria-pressed={textSize === 'small'}
                                color={textSize === 'small' ? 'primary' : 'default'}
                                size="large"
                                sx={{
                                    width: '100%',
                                    border: theme => textSize === 'small' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                    borderRadius: '8px',
                                    flexDirection: 'column',
                                }}
                            >
                                <TextDecreaseIcon />
                                <Typography variant="body1" sx={{ mt: 0.5 }}>Klein</Typography>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid size={1}>
                        <Tooltip title="Mittelgroßer Text">
                            <IconButton
                                onClick={() => handleTextSizeChange('medium')}
                                aria-label="Mittelgroßer Text"
                                aria-pressed={textSize === 'medium'}
                                color={textSize === 'medium' ? 'primary' : 'default'}
                                size="large"
                                sx={{
                                    width: '100%',
                                    border: theme => textSize === 'medium' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                    borderRadius: '8px',
                                    flexDirection: 'column',
                                }}
                            >
                                <FormatSizeIcon />
                                <Typography variant="body1" sx={{ mt: 0.5 }}>Mittel</Typography>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid size={1}>
                        <Tooltip title="Großer Text">
                            <IconButton
                                onClick={() => handleTextSizeChange('large')}
                                aria-label="Großer Text"
                                aria-pressed={textSize === 'large'}
                                color={textSize === 'large' ? 'primary' : 'default'}
                                size="large"
                                sx={{
                                    width: '100%',
                                    border: theme => textSize === 'large' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                                    borderRadius: '8px',
                                    flexDirection: 'column',
                                }}
                            >
                                <TextIncreaseIcon />
                                <Typography variant="body1" sx={{ mt: 0.5 }}>Groß</Typography>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>
        </StyledMenu>
    )
}

const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '12px',
        minWidth: '280px',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
        padding: theme.spacing(2, 0),
        borderBottomLeftRadius: 0,
        backgroundColor: theme.palette.background.paper,
    },
}));
